import SvgPath from 'svgpath'
import { restrokePath, hasStrokeWidth, STROKE } from './strokeWidth'

/**
 * Helpers for rendering icons as inline SVG and exporting them as SVG markup,
 * data URLs, PNG files and framework components.
 *
 * Icon paths are stored in SVG coordinates (y down) inside a square box of
 * `svg.width` units (1000, the font's em). The site and every export draw them
 * on the common 24x24 grid instead. Duotone icons are two glyphs sharing a
 * name: the "before" glyph is drawn solid and the "after" glyph at 40% opacity.
 */

export const DUOTONE_OPACITY = 0.6

// Human-readable icon or category name: "angle-double-left" -> "angle double left"
export const iconLabel = name => name.replace(/-/g, ' ')

// Grid used by Lucide, Material, Heroicons... for both the site and exports
export const ICON_GRID = 24

export const ICON_VIEWBOX = `0 0 ${ICON_GRID} ${ICON_GRID}`

const canCache = typeof WeakMap === 'function'
const gridPathCache = canCache ? new WeakMap() : null

/**
 * Stored path scaled onto the ICON_GRID box and rounded to 0.01.
 * The stored path itself must stay on the 1000 grid: the font package generator uses it.
 * @param  {Object} svg - Icon entity's `svg` ({ width, height?, path })
 * @return {String}
 */
export function gridPath(svg) {
	let d = gridPathCache && gridPathCache.get(svg)
	if (!d) {
		d = new SvgPath(svg.path)
			.scale(ICON_GRID / (svg.height || svg.width))
			.rel()
			.round(2)
			.toString()
		if (gridPathCache) gridPathCache.set(svg, d)
	}
	return d
}

const secondaryCache = canCache ? new WeakMap() : null

/**
 * Find the "after" glyph that completes a duotone icon
 * @param  {Array} icons - Full icon list (store.localstore.icons)
 * @param  {Object} icon - The visible ("before") duotone icon
 * @return {Object|null}
 */
export function findDuotoneSecondary(icons, icon) {
	if (!icon || !icon.isDuotone || icon.assign === 'after' || !Array.isArray(icons)) return null

	let byName = secondaryCache && secondaryCache.get(icons)
	if (!byName) {
		byName = {}
		icons.forEach(item => {
			if (item.isDuotone && item.assign === 'after') byName[item.name] = item
		})
		if (secondaryCache) secondaryCache.set(icons, byName)
	}
	return byName[icon.name] || null
}

/**
 * Normalise an icon entity into the shape every exporter below expects
 * @param  {Object} icon - Icon entity from the store
 * @param  {Object} [secondary] - Duotone "after" glyph, if any
 * @param  {Object} [options]
 * @param  {Number} [options.strokeWidth] - Line icons only: redraw at this width (px)
 * @return {Object} { name, isDuotone, viewBox, paths: [{ d, opacity }] }
 */
export function toIconShape(icon, secondary = null, { strokeWidth = STROKE.base } = {}) {
	const d = gridPath(icon.svg)
	const paths = [{ d: hasStrokeWidth(icon) ? restrokePath(d, strokeWidth) : d, opacity: 1 }]
	if (secondary) paths.push({ d: gridPath(secondary.svg), opacity: DUOTONE_OPACITY })

	return {
		name: icon.name,
		isDuotone: !!icon.isDuotone,
		viewBox: ICON_VIEWBOX,
		paths
	}
}

const SVG_NS = 'http://www.w3.org/2000/svg'

const pathTags = (shape, opacityAttr) =>
	shape.paths.map(path =>
		path.opacity === 1 ? `<path d="${path.d}" />` : `<path d="${path.d}" ${opacityAttr(path.opacity)} />`
	)

const indent = (lines, spaces) => lines.map(line => ' '.repeat(spaces) + line).join('\n')

const pascalCase = name =>
	name
		.split(/[^a-zA-Z0-9]+/)
		.filter(Boolean)
		.map(part => part.charAt(0).toUpperCase() + part.slice(1))
		.join('')

/** Component name, e.g. IcofontAccessLevels or IcofontDuotoneAccessLevels */
export const componentName = shape =>
	'Icofont' + (shape.isDuotone ? 'Duotone' : '') + pascalCase(shape.name)

const kebabName = shape => 'icofont-' + (shape.isDuotone ? 'duotone-' : '') + shape.name

export function toSvg(shape, { size = 24, color = 'currentColor' } = {}) {
	return (
		`<svg xmlns="${SVG_NS}" viewBox="${shape.viewBox}" width="${size}" height="${size}" fill="${color}">` +
		pathTags(shape, opacity => `opacity="${opacity}"`).join('') +
		'</svg>'
	)
}

export const toDataUrl = shape => 'data:image/svg+xml,' + encodeURIComponent(toSvg(shape, { color: '#000' }))

const svgElementLines = (shape, extraAttrs, opacityAttr) => [
	`<svg xmlns="${SVG_NS}" viewBox="${shape.viewBox}" width="1em" height="1em" fill="currentColor"${extraAttrs}>`,
	...pathTags(shape, opacityAttr).map(tag => '  ' + tag),
	'</svg>'
]

export function toJsx(shape) {
	const svg = svgElementLines(shape, ' {...props}', opacity => `opacity={${opacity}}`)
	return [
		`export const ${componentName(shape)} = (props) => (`,
		indent(svg, 2),
		');',
		''
	].join('\n')
}

export function toVue(shape) {
	const svg = svgElementLines(shape, '', opacity => `opacity="${opacity}"`)
	return ['<template>', indent(svg, 2), '</template>', ''].join('\n')
}

export function toSvelte(shape) {
	return svgElementLines(shape, ' {...$$restProps}', opacity => `opacity="${opacity}"`).join('\n') + '\n'
}

export function toAngular(shape) {
	const svg = svgElementLines(shape, '', opacity => `opacity="${opacity}"`)
	return [
		"import { Component } from '@angular/core';",
		'',
		'@Component({',
		`  selector: '${kebabName(shape)}',`,
		'  standalone: true,',
		"  host: { style: 'display: inline-flex' },",
		'  template: `',
		indent(svg, 4),
		'  `,',
		'})',
		`export class ${componentName(shape)}Component {}`,
		''
	].join('\n')
}

function saveBlob(blob, filename) {
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = filename
	document.body.appendChild(link)
	link.click()
	link.remove()
	setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export const fileName = (shape, extension) => `${kebabName(shape)}.${extension}`

export function downloadSvg(shape) {
	const svg = '<?xml version="1.0" encoding="UTF-8"?>\n' + toSvg(shape, { color: '#000' }) + '\n'
	saveBlob(new Blob([svg], { type: 'image/svg+xml' }), fileName(shape, 'svg'))
}

/**
 * Render the icon onto a canvas and save it as a transparent PNG
 * @return {Promise}
 */
export function downloadPng(shape, size = 512) {
	return new Promise((resolve, reject) => {
		const image = new Image()
		image.onload = () => {
			const canvas = document.createElement('canvas')
			canvas.width = size
			canvas.height = size
			canvas.getContext('2d').drawImage(image, 0, 0, size, size)
			canvas.toBlob(blob => {
				if (!blob) return reject(new Error('Could not render PNG'))
				saveBlob(blob, fileName(shape, 'png'))
				resolve()
			}, 'image/png')
		}
		image.onerror = () => reject(new Error('Could not render PNG'))
		image.src = 'data:image/svg+xml,' + encodeURIComponent(toSvg(shape, { size, color: '#000' }))
	})
}
